import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../components/hooks/useAuth';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import TeamUserTable from './components/TeamUserTable';
import EditUserModal from './components/EditUserModal';
import CenterMembershipModal from './components/CenterMembershipModal';
import BulkActionsPanel from './components/BulkActionsPanel';
import {
  getAllUsers,
  updateUser,
  getCenters,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'admin', label: 'Admin/Owner' },
  { value: 'center_manager', label: 'Center Manager Sales' },
  { value: 'center_manager_ops', label: 'Center Manager Ops' },
  { value: 'sales', label: 'Sales' },
  { value: 'operations', label: 'Operations' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// ─── Supabase helpers ─────────────────────────────────────────────────────────
async function getUserCenterMemberships(userId) {
  const { data, error } = await supabase
    ?.from('center_memberships')
    ?.select('id, center_id, role, centers(id, name, city, location)')
    ?.eq('user_id', userId);
  if (error) throw error;
  return (data || [])?.map((m) => ({
    id: m?.id,
    centerId: m?.center_id,
    role: m?.role,
    centerName: m?.centers?.name,
    city: m?.centers?.city || m?.centers?.location,
  }));
}

async function syncUserCenterMemberships(userId, centerIds) {
  // Fetch existing memberships
  const { data: existing } = await supabase
    ?.from('center_memberships')
    ?.select('id, center_id')
    ?.eq('user_id', userId);

  const existingIds = (existing || [])?.map((m) => m?.center_id);
  const toAdd = centerIds?.filter((id) => !existingIds?.includes(id));
  const toRemove = existingIds?.filter((id) => !centerIds?.includes(id));

  if (toRemove?.length > 0) {
    await supabase
      ?.from('center_memberships')
      ?.delete()
      ?.eq('user_id', userId)
      ?.in('center_id', toRemove);
  }

  if (toAdd?.length > 0) {
    const rows = toAdd?.map((centerId) => ({ user_id: userId, center_id: centerId, role: 'member' }));
    await supabase?.from('center_memberships')?.insert(rows);
  }
}

async function bulkAssignToCenter(userIds, centerId) {
  const { data: existing } = await supabase
    ?.from('center_memberships')
    ?.select('user_id')
    ?.eq('center_id', centerId)
    ?.in('user_id', userIds);

  const existingUserIds = (existing || [])?.map((m) => m?.user_id);
  const toAdd = userIds?.filter((id) => !existingUserIds?.includes(id));

  if (toAdd?.length > 0) {
    const rows = toAdd?.map((userId) => ({ user_id: userId, center_id: centerId, role: 'member' }));
    await supabase?.from('center_memberships')?.insert(rows);
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
const TeamManagement = () => {
  const { userProfile } = useAuth();

  const [users, setUsers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selection
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Modals
  const [editUser, setEditUser] = useState(null);
  const [membershipUser, setMembershipUser] = useState(null);
  const [membershipData, setMembershipData] = useState([]);

  // ── Data loading ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {
        role: roleFilter !== 'all' ? roleFilter : undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        search: searchTerm?.trim() || undefined,
      };
      const [usersData, centersData] = await Promise.all([
        getAllUsers(filters),
        getCenters(),
      ]);

      // Enrich users with center memberships
      const enriched = await Promise.all(
        (usersData || [])?.map(async (u) => {
          try {
            const memberships = await getUserCenterMemberships(u?.id);
            return { ...u, centerMemberships: memberships };
          } catch {
            return { ...u, centerMemberships: [] };
          }
        })
      );

      setUsers(enriched);
      setCenters(centersData || []);
    } catch (err) {
      console.error('TeamManagement fetch error:', err);
      setError(err?.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, statusFilter, searchTerm]);

  useEffect(() => {
    if (['super_admin', 'admin']?.includes(userProfile?.role)) {
      fetchData();
    }
  }, [userProfile, fetchData]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e?.preventDefault();
    fetchData();
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUsers((prev) =>
      checked ? [...prev, userId] : prev?.filter((id) => id !== userId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedUsers(checked ? users?.map((u) => u?.id) : []);
  };

  const handleEditUser = (user) => setEditUser(user);

  const handleChangeRole = (user) => setEditUser(user);

  const handleManageCenters = async (user) => {
    try {
      const memberships = await getUserCenterMemberships(user?.id);
      setMembershipData(memberships);
      setMembershipUser(user);
    } catch (err) {
      setError(err?.message || 'Failed to load memberships');
    }
  };

  const handleSaveUser = async (userId, updates) => {
    try {
      setActionLoading(true);
      await updateUser(userId, updates);
      setEditUser(null);
      showSuccess('User updated successfully');
      fetchData();
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveMemberships = async (userId, centerIds) => {
    try {
      setActionLoading(true);
      await syncUserCenterMemberships(userId, centerIds);
      setMembershipUser(null);
      setMembershipData([]);
      showSuccess('Center memberships updated successfully');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to update memberships');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkRoleUpdate = async (role) => {
    if (selectedUsers?.length === 0) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedUsers?.map((id) => updateUser(id, { role })));
      setSelectedUsers([]);
      showSuccess(`Role updated for ${selectedUsers?.length} user(s)`);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to update roles');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkCenterAssign = async (centerId) => {
    if (selectedUsers?.length === 0) return;
    try {
      setActionLoading(true);
      await bulkAssignToCenter(selectedUsers, centerId);
      setSelectedUsers([]);
      const center = centers?.find((c) => c?.id === centerId);
      showSuccess(`${selectedUsers?.length} user(s) assigned to ${center?.name || 'center'}`);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to assign users to center');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Access guard ────────────────────────────────────────────────────────────
  if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500">Only administrators can access Team Management.</p>
        </div>
      </div>
    );
  }

  // ── Stats ────────────────────────────────────────────────────────────────────
  const totalUsers = users?.length;
  const activeUsers = users?.filter((u) => u?.isActive)?.length;
  const usersWithCenters = users?.filter((u) => u?.centerMemberships?.length > 0)?.length;
  const unassignedUsers = users?.filter((u) => u?.centerMemberships?.length === 0)?.length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-500 text-sm mt-1">Manage users, roles, and center memberships</p>
        </div>
        <Button onClick={fetchData} variant="secondary" className="flex items-center gap-2">
          <Icon name="RefreshCw" size={15} />
          Refresh
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Icon name="CheckCircle" size={18} className="text-green-500 flex-shrink-0" />
          <p className="text-green-700 text-sm">{successMsg}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: totalUsers, icon: 'Users', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Active Users', value: activeUsers, icon: 'UserCheck', color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Assigned to Centers', value: usersWithCenters, icon: 'Building', color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Unassigned', value: unassignedUsers, icon: 'UserX', color: 'text-orange-600', bg: 'bg-orange-50' },
        ]?.map((stat) => (
          <div key={stat?.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${stat?.bg} flex items-center justify-center flex-shrink-0`}>
              <Icon name={stat?.icon} size={20} className={stat?.color} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">{stat?.label}</p>
              <p className={`text-xl font-bold ${stat?.color}`}>{loading ? '—' : stat?.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <div className="relative">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                placeholder="Name or email..."
                className="pl-9"
              />
            </div>
          </div>
          <div className="w-44">
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e?.target?.value)}>
              {ROLE_FILTER_OPTIONS?.map((opt) => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </Select>
          </div>
          <div className="w-36">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e?.target?.value)}>
              {STATUS_FILTER_OPTIONS?.map((opt) => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </Select>
          </div>
          <Button type="submit" variant="primary" className="flex items-center gap-2">
            <Icon name="Search" size={14} />
            Search
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => { setSearchTerm(''); setRoleFilter('all'); setStatusFilter('all'); }}
          >
            Reset
          </Button>
        </form>
      </div>

      {/* Bulk Actions */}
      {selectedUsers?.length > 0 && (
        <BulkActionsPanel
          selectedCount={selectedUsers?.length}
          centers={centers}
          onBulkRoleUpdate={handleBulkRoleUpdate}
          onBulkCenterAssign={handleBulkCenterAssign}
          onClearSelection={() => setSelectedUsers([])}
          loading={actionLoading}
        />
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : (
        <TeamUserTable
          users={users}
          selectedUsers={selectedUsers}
          onSelectUser={handleSelectUser}
          onSelectAll={handleSelectAll}
          onEditUser={handleEditUser}
          onChangeRole={handleChangeRole}
          onManageCenters={handleManageCenters}
          currentUserRole={userProfile?.role}
        />
      )}

      {/* Modals */}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSave={handleSaveUser}
          loading={actionLoading}
        />
      )}
      {membershipUser && (
        <CenterMembershipModal
          user={membershipUser}
          centers={centers}
          currentMemberships={membershipData}
          onClose={() => { setMembershipUser(null); setMembershipData([]); }}
          onSave={handleSaveMemberships}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default TeamManagement;
